/**
 * FI Notifications Table Component
 *
 * Data table with unified search for FI notifications
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Download, X } from 'lucide-react';
import { Table, Button, SearchBar, DatePicker, type TableColumn } from 'equal-ds-ui';
import { PAGE_SIZE_OPTIONS } from '../../../constants/pagination.constants';
import { fiNotificationsAPI } from '../../../services/fi-notifications-api.service';
import { downloadAPI } from '../../../services/download-api.service';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { Toast } from '../../ui/Toast';
import type {
  FINotificationsTableProps,
  FINotification,
  FINotificationCSVData,
  DateRange,
} from '../../../types/fi-notifications';

/**
 * Format date to readable format
 */
const formatCreatedAt = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch (error) {
    return dateString;
  }
};

/**
 * Filter notifications based on search query and domain
 */
const filterNotifications = (
  notifications: FINotification[],
  searchQuery: string,
  searchDomain: string
): FINotification[] => {
  if (!searchQuery.trim()) {
    return notifications;
  }

  const query = searchQuery.toLowerCase();

  return notifications.filter((notification) => {
    switch (searchDomain) {
      case 'notifier':
        return notification.notifier_id.toLowerCase().includes(query);
      case 'consent':
        return notification.consent_id.toLowerCase().includes(query);
      case 'session':
        return notification.fi_n_session_id.toLowerCase().includes(query);
      case 'transaction':
        return notification.txn_id.toLowerCase().includes(query);
      case 'status':
        return notification.fi_n_status.toLowerCase().includes(query);
      case 'all':
      default:
        // Search across all fields
        return (
          notification.notifier_id.toLowerCase().includes(query) ||
          notification.consent_id.toLowerCase().includes(query) ||
          notification.fi_n_session_id.toLowerCase().includes(query) ||
          notification.txn_id.toLowerCase().includes(query) ||
          notification.fi_n_status.toLowerCase().includes(query) ||
          formatCreatedAt(notification.createdAt).toLowerCase().includes(query)
        );
    }
  });
};

/**
 * Get consent ID from notification
 */
const getConsentId = (notification: FINotification): string => {
  if (notification.consent_data_request?.accounts?.length > 0) {
    return notification.consent_data_request.accounts[0].consent_id;
  }
  return '';
};

/**
 * Calculate days difference between two dates
 */
const getDaysDifference = (startDate: Date, endDate: Date): number => {
  const timeDiff = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.round(timeDiff / (1000 * 3600 * 24));
};

/**
 * Generate CSV content from notifications data
 */
const generateCSVContent = (notifications: FINotification[]): string => {
  // Transform data to CSV format
  const csvData: FINotificationCSVData[] = notifications.map((notification) => ({
    'Notifier Id': notification.notifier_id || '',
    'Created On': notification.createdAt || '',
    'Consent Id': getConsentId(notification),
    'Session Id': notification.fi_n_session_id || '',
    'Session Status': notification.fi_n_status || '',
    'Transaction Id': notification.txn_id || '',
  }));

  if (csvData.length === 0) {
    return '';
  }

  // Get headers from first row
  const headers = Object.keys(csvData[0]);

  // Create CSV string
  const replacer = (_key: string, value: any) => (value === null ? '' : value);
  const csvString = [
    headers.join(','), // header row
    ...csvData.map((row) =>
      headers
        .map((fieldName) => JSON.stringify(row[fieldName as keyof FINotificationCSVData], replacer))
        .join(',')
    ),
  ].join('\r\n');

  return csvString;
};

/**
 * Trigger browser download of CSV file
 */
const downloadCSVFile = (csvContent: string, filename: string = 'fi_notification_list.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const downloadURL = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadURL;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(downloadURL);
};

export const FINotificationsTable: React.FC<FINotificationsTableProps> = ({
  data,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onViewDetails,
  isLoading,
  canShowAccountDetails,
  canDownloadCSV,
}) => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDomain, setSearchDomain] = useState('all');

  // Date picker modal state
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
  });
  const [dateError, setDateError] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState(false);

  // Toast state
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Error handling
  const { handleError } = useErrorHandler();

  // Feature flag
  const isJobBasedDownloadEnabled = sessionStorage.getItem('isJobBasedDownloadEnabled') === 'true';

  // Search bar domains
  const domains = [
    { value: 'all', label: 'All Fields' },
    { value: 'notifier', label: 'Notifier ID' },
    { value: 'consent', label: 'Consent ID' },
    { value: 'session', label: 'Session ID' },
    { value: 'transaction', label: 'Transaction ID' },
    { value: 'status', label: 'Status' },
  ];

  // Apply client-side filtering
  const filteredData = useMemo(() => {
    return filterNotifications(data, searchQuery, searchDomain);
  }, [data, searchQuery, searchDomain]);

  /**
   * Handle download CSV button click - show date picker modal
   */
  const handleDownloadClick = () => {
    console.log('handleDownloadClick');
    setShowDateModal(true);
    setDateError('');
  };

  /**
   * Handle cancel date picker modal
   */
  const handleCancelDateModal = () => {
    setShowDateModal(false);
    setDateError('');
    // Reset date range to default (last 30 days)
    setDateRange({
      startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
      endDate: new Date(),
    });
  };

  /**
   * Format date to YYYY-MM-DD for new download-service API
   */
  const formatDateISO = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  /**
   * Format date to MM-DD-YYYY for legacy CSV download API
   */
  const formatDateLegacy = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  /**
   * New flow: Create a download job via download-service
   */
  const handleJobBasedDownload = useCallback(async (startDate: Date, endDate: Date) => {
    try {
      await downloadAPI.createJob({
        download_type: 'FI_NOTIFICATIONS',
        file_type: 'EXCEL',
        filters: {
          from: formatDateISO(startDate),
          to: formatDateISO(endDate),
        },
      });
      setShowDateModal(false);
      setToastMessage('Report queued! Check the Download Centre');
    } catch (err) {
      handleError(err, { component: 'FINotificationsTable', action: 'createDownloadJob' });
      setDateError('Failed to create download job. Please try again.');
    }
  }, [handleError]);

  /**
   * Old flow: Download CSV directly in-browser
   */
  const handleDirectCSVDownload = useCallback(async (startDate: Date, endDate: Date) => {
    try {
      const response = await fiNotificationsAPI.downloadFINotificationsData({
        from: formatDateLegacy(startDate),
        to: formatDateLegacy(endDate),
        all: true,
      });

      const csvRows = response.data.rows || [];

      if (csvRows.length === 0) {
        setDateError('No data found for the selected date range');
        return;
      }

      const csvContent = generateCSVContent(csvRows);
      downloadCSVFile(csvContent);
      setShowDateModal(false);
      handleCancelDateModal();
    } catch (err) {
      handleError(err, { component: 'FINotificationsTable', action: 'downloadCSV' });
      setDateError('Failed to download CSV. Please try again.');
    }
  }, [handleError, handleCancelDateModal]);

  /**
   * Handle download CSV with selected date range
   */
  const handleDownloadCSV = async () => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setDateError('Please select both start and end dates');
      return;
    }

    const daysDiff = getDaysDifference(dateRange.startDate, dateRange.endDate);
    if (daysDiff > 31) {
      setDateError('Please select range between 31 days');
      return;
    }

    setIsDownloading(true);
    setDateError('');

    try {
      if (isJobBasedDownloadEnabled) {
        await handleJobBasedDownload(dateRange.startDate, dateRange.endDate);
      } else {
        await handleDirectCSVDownload(dateRange.startDate, dateRange.endDate);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  // Define table columns (simplified without inline filters due to Equal DS API constraints)
  const columns: TableColumn<FINotification>[] = useMemo(() => {
    const cols: TableColumn<FINotification>[] = [];

    // Customer Accounts column (conditionally shown based on permissions)
    if (canShowAccountDetails) {
      cols.push({
        key: 'customer_accounts',
        header: 'Customer Accounts',
        width: 180,
        locked: true,
        render: (_value, row) => (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onViewDetails(row)}
          >
            Account Details
          </Button>
        ),
      });
    }

    // Notifier ID column
    cols.push({
      key: 'notifier_id',
      header: 'Notifier Id',
      width: 120,
      render: (_value, row) => <span className="text-sm">{row.notifier_id}</span>,
    });

    // Created On column
    cols.push({
      key: 'createdAt',
      header: 'Created On',
      width: 120,
      render: (_value, row) => <span className="text-sm">{formatCreatedAt(row.createdAt)}</span>,
    });

    // Consent ID column
    cols.push({
      key: 'consent_id',
      header: 'Consent Id',
      width: 300,
      render: (_value, row) => <span className="text-sm">{row.consent_id}</span>,
    });

    // Session ID column
    cols.push({
      key: 'fi_n_session_id',
      header: 'Session Id',
      width: 400,
      render: (_value, row) => <span className="text-sm">{row.fi_n_session_id}</span>,
    });

    // Session Status column
    cols.push({
      key: 'fi_n_status',
      header: 'Session Status',
      width: 120,
      render: (_value, row) => <span className="text-sm">{row.fi_n_status}</span>,
    });

    // Transaction ID column
    cols.push({
      key: 'txn_id',
      header: 'Transaction Id',
      width: 300,
      render: (_value, row) => <span className="text-sm">{row.txn_id}</span>,
    });

    return cols;
  }, [onViewDetails, canShowAccountDetails]);

  return (
    <>
      <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-lg bg-white">
        {/* Header with Search and Actions */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1">
            <SearchBar
              variant="with-dropdown"
              domains={domains}
              value={searchQuery}
              onChange={setSearchQuery}
              selectedDomain={searchDomain}
              onDomainChange={setSearchDomain}
              placeholder="Search notifications..."
            />
          </div>
          {canDownloadCSV && (
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={handleDownloadClick}
                disabled={isDownloading}
              >
                <Download className="w-6 h-6" />
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <Table
            className="flex-1 min-h-0 shadow-sm rounded-xl"
            data={filteredData}
            columns={columns}
            loading={isLoading}
            columnManager={{
              enabled: true,
              triggerLabel: 'Manage columns',
              allowHiding: true,
            }}
            pagination={{
              currentPage,
              pageSize,
              totalItems,
              onPageChange,
              onPageSizeChange,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
            }}
          />
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage}
          variant="success"
          onClose={() => setToastMessage(null)}
        />
      )}

      {/* Date Range Picker Modal */}
      {showDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{isJobBasedDownloadEnabled ? 'Download Report' : 'Download CSV'}</h3>
              <button
                onClick={handleCancelDateModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isDownloading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="space-y-4">
                {/* Date Range Pickers */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      From Date
                    </label>
                    <DatePicker
                      selectedDate={dateRange.startDate || undefined}
                      onChange={(date) => {
                        setDateRange({ ...dateRange, startDate: date || null });
                        setDateError('');
                      }}
                      placeholder="Select start date"
                      maxDate={dateRange.endDate || new Date()}
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To Date
                    </label>
                    <DatePicker
                      selectedDate={dateRange.endDate || undefined}
                      onChange={(date) => {
                        setDateRange({ ...dateRange, endDate: date || null });
                        setDateError('');
                      }}
                      placeholder="Select end date"
                      minDate={dateRange.startDate || undefined}
                      maxDate={new Date()}
                    />
                  </div>
                </div>

                {/* Info Message */}
                {!isJobBasedDownloadEnabled && (
                  <div className="text-sm text-orange-600 bg-orange-50 p-3 rounded-md">
                    Maximum 10000 rows data will be able to download in CSV file.
                  </div>
                )}

                {/* Error Message */}
                {dateError && (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                    {dateError}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
              <Button
                variant="secondary"
                onClick={handleCancelDateModal}
                disabled={isDownloading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDownloadCSV}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
